import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  MessageCircle,
  Phone,
  Home,
  ExternalLink,
  Loader2,
  AlertTriangle,
  Truck,
  Star,
  CheckCircle,
  Camera,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { OrderTimeline } from '../../components/shopper/OrderTimeline';
import { OrderDeliveryMapCard } from '../../components/shared/OrderDeliveryMapCard';
import { useOrderMapLocations } from '../../hooks/useOrderMapLocations';
import { useOrder } from '../../hooks/useOrders';
import { useDelivery } from '../../hooks/useDelivery';
import { BackButton } from '../../components/ui/BackButton';
import { SHOPPER_HOME } from '../../constants/routes';
import { cn, formatEtaClock, formatBatchStop } from '../../lib/utils';

/** Star-rating card shown once an order is delivered. */
function ReviewCard({ orderId, businessName }: { orderId: string; businessName?: string }) {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setError(null);
    try {
      const review = httpsCallable(functions, 'submitOrderReview');
      await review({ orderId, rating, comment: comment.trim() || undefined });
      setDone(true);
    } catch (err: any) {
      if (err?.code === 'functions/already-exists') {
        setDone(true);
      } else {
        setError(err?.message || 'Could not submit your review.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="p-4 flex items-center space-x-3">
          <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
          <p className="text-sm text-heading">Thanks for your review!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">How was your order?</CardTitle>
        {businessName && <p className="text-sm text-body">Rate your experience with {businessName}</p>}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-center space-x-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              className="p-1"
            >
              <Star
                className={cn(
                  'h-8 w-8 transition-colors',
                  star <= (hovered || rating)
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-gray-300'
                )}
              />
            </button>
          ))}
        </div>
        {rating > 0 && (
          <>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share a few words about your experience (optional)"
              rows={2}
              className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light resize-none"
            />
            {error && <p className="text-xs text-red-600">{error}</p>}
            <Button fullWidth onClick={submit} loading={submitting}>
              Submit Review
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// States from which a shopper may still cancel (pre-pickup).
// Mirrors SHOPPER_CANCEL entries in the backend TRANSITION_TABLE.
const CANCELLABLE_STATES = [
  'CREATED',
  'CART_VALIDATED',
  'INVENTORY_RESERVED',
  'PRICED',
  'PAYMENT_AUTHORIZED',
  'PENDING_BUSINESS_APPROVAL',
  'BUSINESS_APPROVED',
  'DISPATCHING_DRIVER',
  'DRIVER_ASSIGNED',
];

export function OrderTrackingPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { order, loading, error } = useOrder(orderId || null);
  const { checkStatus, statusLoading } = useDelivery();
  const mapLocations = useOrderMapLocations(order);

  // Cancel flow state
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleCancelOrder = async () => {
    if (!orderId || cancelling) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const cancel = httpsCallable(functions, 'cancelOrder');
      await cancel({
        orderId,
        reason: 'Cancelled by customer',
        clientRequestId: `cancel_${orderId}_${Date.now()}`,
      });
      setConfirmingCancel(false);
      // Order state updates live via the useOrder subscription
    } catch (err: any) {
      setCancelError(err?.message || 'Unable to cancel this order.');
    } finally {
      setCancelling(false);
    }
  };

  // ─── Loading state ────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-body">Loading order...</p>
        </div>
      </div>
    );
  }

  // ─── Error / not found ────────────────────────────────────
  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-serif text-heading mb-2">Order not found</h2>
          <p className="text-body mb-6">{error || 'This order may have been removed.'}</p>
          <Button onClick={() => navigate(SHOPPER_HOME)}>Go Home</Button>
        </div>
      </div>
    );
  }

  // ─── Derived state ────────────────────────────────────────
  const state = order.state;
  const uber = order.uber;
  const courier = uber?.courier;
  const trackingUrl = order.trackingUrl || uber?.trackingUrl;
  const eta = uber?.dropoffEta;

  const canCancel = CANCELLABLE_STATES.includes(state);
  const canDispute = ['DELIVERED', 'COMPLETED'].includes(state);
  const isDelivered = ['DELIVERED', 'COMPLETED'].includes(state);
  const showReview = isDelivered && !order.flags?.reviewed && !order.flags?.disputed;
  const proofPhotoUrl = order.deliveryProof?.photoUrl || null;
  const showCourierCard = !!courier?.name && ['DRIVER_ASSIGNED', 'PICKED_UP', 'OUT_FOR_DELIVERY'].includes(state);
  const batchStop = formatBatchStop(uber?.batchId, uber?.dropoffSequence, uber?.totalDropoffCount);
  const showMap = ['DISPATCHING_DRIVER', 'DRIVER_ASSIGNED', 'PICKED_UP', 'OUT_FOR_DELIVERY'].includes(state);

  const dropoffClock = formatEtaClock(uber?.dropoffEta);
  const baseMapLabel =
    state === 'DISPATCHING_DRIVER'
      ? 'Finding a courier'
      : state === 'DRIVER_ASSIGNED'
        ? 'Courier heading to store'
        : state === 'PICKED_UP'
          ? 'Order picked up'
          : state === 'OUT_FOR_DELIVERY'
            ? 'On the way to you!'
            : undefined;
  // Append the delivery ETA so the shopper sees arrival time on the map itself.
  const mapStatusLabel =
    baseMapLabel && dropoffClock && state !== 'DISPATCHING_DRIVER'
      ? `${baseMapLabel} · ETA ${dropoffClock}`
      : baseMapLabel;
  const isTerminal = ['CANCELLED', 'REFUNDED', 'DISPUTED'].includes(state);
  // Only warn about a dispatch problem while we're actually still looking for a
  // courier. Once one is assigned (or the order has moved past dispatch), a
  // lingering flag from an earlier retry must not show a false "issue" banner.
  const dispatchFailed =
    order.flags?.dispatchFailed &&
    state === 'DISPATCHING_DRIVER' &&
    !courier?.name;

  // Format address for display
  const addr = order.deliveryAddress;
  const displayAddress = addr
    ? `${addr.street || addr.line1 || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || addr.zip || ''}`
    : 'Address not available';

  // Format ETA
  const displayEta = formatEtaClock(eta);

  // Handle manual status refresh
  const handleRefreshStatus = async () => {
    if (orderId) await checkStatus(orderId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BackButton fallback="/orders" className="p-2 -ml-2 hover:bg-gray-100 rounded-full" />
              <div className="ml-4">
                <h1 className="text-lg font-serif text-heading">
                  Order #{(orderId || '').slice(-6).toUpperCase()}
                </h1>
                {displayEta && (
                  <p className="text-sm text-body">Estimated delivery: ~{displayEta}</p>
                )}
                {!displayEta && !isTerminal && (
                  <p className="text-sm text-body capitalize">{state.replace(/_/g, ' ').toLowerCase()}</p>
                )}
              </div>
            </div>
            <Link to={SHOPPER_HOME} className="p-2 hover:bg-gray-100 rounded-full" aria-label="Home">
              <Home className="h-6 w-6 text-heading" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* ─── Delivery Proof (after delivery) ─────────────────── */}
        {isDelivered && proofPhotoUrl && (
          <Card className="overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center space-x-2">
                <Camera className="h-5 w-5 text-primary" />
                <span>Delivery Proof</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-xl overflow-hidden bg-gray-100">
                <img
                  src={proofPhotoUrl}
                  alt="Delivery proof"
                  className="w-full max-h-80 object-contain"
                />
              </div>
              {order.deliveryProof?.notes && (
                <p className="text-sm text-body mt-2">{order.deliveryProof.notes}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* ─── Review prompt (after delivery) ──────────────────── */}
        {showReview && orderId && (
          <ReviewCard orderId={orderId} businessName={order.businessName} />
        )}

        {/* ─── Live Tracking Map / Banner ───────────────────────── */}
        {showMap && (
          <>
            <OrderDeliveryMapCard
              store={mapLocations.store}
              dropoff={mapLocations.dropoff}
              courier={mapLocations.courier}
              isLoaded={mapLocations.isLoaded}
              geocoding={mapLocations.geocoding}
              hasAnyPoint={mapLocations.hasAnyPoint}
              statusLabel={mapStatusLabel}
            />
            {trackingUrl && (
              <Card>
                <CardContent className="py-3">
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2 text-sm text-primary font-medium hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Open live tracking</span>
                  </a>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ─── Dispatch failed warning ─────────────────────────── */}
        {dispatchFailed && (
          <Card className="border-warning/30 bg-warning/5">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-heading text-sm">Courier dispatch issue</p>
                  <p className="text-sm text-body mt-1">
                    We're working on assigning a courier. You'll be notified when one is on the way.
                  </p>
                  {uber?.dispatchError && (
                    <p className="text-xs text-body mt-1 opacity-70">{uber.dispatchError}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── Uber Courier Card ───────────────────────────────── */}
        {showCourierCard && courier && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Courier avatar */}
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                    {courier.imgUrl ? (
                      <img
                        src={courier.imgUrl}
                        alt={courier.name || 'Courier'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Truck className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-heading">{courier.name}</p>
                    <div className="flex items-center space-x-1 text-sm text-body">
                      <span>Uber courier</span>
                      {courier.vehicleType && (
                        <>
                          <span>&middot;</span>
                          <span className="capitalize">{courier.vehicleType}</span>
                        </>
                      )}
                    </div>
                    {batchStop && (
                      <p className="text-xs text-amber-600 mt-1">
                        {batchStop} · courier has other deliveries, so timing may vary
                      </p>
                    )}
                  </div>
                </div>
                {/* Contact buttons — Uber provides courier phone */}
                {courier.phone && (
                  <div className="flex space-x-2">
                    <Button variant="outline" size="icon" asChild>
                      <a href={`sms:${courier.phone}`}>
                        <MessageCircle className="h-5 w-5" />
                      </a>
                    </Button>
                    <Button variant="outline" size="icon" asChild>
                      <a href={`tel:${courier.phone}`}>
                        <Phone className="h-5 w-5" />
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── Order Timeline ──────────────────────────────────── */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Order Status</CardTitle>
            {uber?.deliveryId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefreshStatus}
                disabled={statusLoading}
                className="text-xs"
              >
                {statusLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <OrderTimeline currentState={state} courierName={courier?.name} />
          </CardContent>
        </Card>

        {/* ─── Order Details ───────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-body mb-2">Items</p>
              {(order.items || []).map((item: any, idx: number) => (
                <div key={idx} className="flex justify-between text-sm py-1">
                  <span className="text-heading">
                    {item.quantity}x {item.name}
                    {item.size && <span className="text-body"> ({item.size})</span>}
                  </span>
                  <span className="text-heading font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              {order.deliveryFee > 0 && (
                <div className="flex justify-between text-sm py-1 text-body">
                  <span>Delivery</span>
                  {/* deliveryFee is stored in dollars (display field) */}
                  <span>${order.deliveryFee.toFixed(2)}</span>
                </div>
              )}
              {(order.pricing?.discountCents || 0) > 0 && (
                <div className="flex justify-between text-sm py-1 text-green-700">
                  <span>Promo Discount{order.promo?.code ? ` (${order.promo.code})` : ''}</span>
                  <span>&minus;${(order.pricing.discountCents / 100).toFixed(2)}</span>
                </div>
              )}
              {(order.driverTip || 0) > 0 && (
                <div className="flex justify-between text-sm py-1 text-body">
                  <span>Driver Tip</span>
                  {/* driverTip is stored in cents */}
                  <span>${(order.driverTip / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-gray-100 mt-3 pt-3 flex justify-between font-medium">
                <span className="text-heading">Total</span>
                <span className="text-heading">${(order.total || 0).toFixed(2)}</span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm text-body mb-1">Delivery Address</p>
              <p className="text-heading">{displayAddress}</p>
            </div>

            {/* Uber delivery ID for reference */}
            {uber?.deliveryId && (
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-body mb-1">Delivery ID</p>
                <p className="text-xs text-body font-mono">{uber.deliveryId}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ─── Actions ─────────────────────────────────────────── */}
        <div className="space-y-3">
          {cancelError && (
            <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{cancelError}</p>
            </div>
          )}

          {canCancel && !confirmingCancel && (
            <Button
              variant="outline"
              fullWidth
              className="text-error hover:bg-error/10 hover:border-error"
              onClick={() => {
                setCancelError(null);
                setConfirmingCancel(true);
              }}
            >
              Cancel Order
            </Button>
          )}

          {canCancel && confirmingCancel && (
            <Card className="border-error/30 bg-error/5">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm text-heading font-medium">
                  Cancel this order?
                </p>
                <p className="text-xs text-body">
                  Your payment hold will be released and any reserved items returned to stock. This can't be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setConfirmingCancel(false)}
                    disabled={cancelling}
                  >
                    Keep Order
                  </Button>
                  <Button
                    fullWidth
                    onClick={handleCancelOrder}
                    disabled={cancelling}
                    className="bg-error hover:bg-error/90 text-white"
                  >
                    {cancelling ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      'Yes, Cancel'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {canDispute && (
            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate(`/orders/${orderId}/dispute`)}
            >
              Report an Issue
            </Button>
          )}

          {/* Open Uber tracking in new tab */}
          {trackingUrl && (
            <Button variant="outline" fullWidth asChild>
              <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Track on Uber
              </a>
            </Button>
          )}

          <Button variant="ghost" fullWidth>
            Need Help?
          </Button>

          <div className="text-center pt-4">
            <Link to={SHOPPER_HOME} className="text-primary font-medium hover:underline">
              Continue Shopping
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
